import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, RefreshCw, Copy, Check, Trash2, AlertCircle, Laptop, Clock, Download,
} from 'lucide-react';
import XBeeProvisioningKit from './XBeeProvisioningKit';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import espnowService, {
  type PairingCode, type NewPairingCode, type BridgeRelease,
} from '../../services/espnowIrrigation';
import orchardService from '../../services/orchard';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import { toNumOr } from '@/utils/numeric';

/**
 * Bring an XBee site online - a laptop, or a Raspberry Pi built here and
 * shipped out.
 *
 * ESP-NOW controllers are added by MAC, because the MAC is printed on the
 * board. An XBee site's identity is its coordinator's 64-bit address, which
 * nobody is going to read off a USB stick in a shed. So instead an admin mints
 * a single-use code here, and the bridge presents that code together with the
 * address it read over serial.
 *
 * The code is the only credential that ever travels, which is what lets the
 * installer binary be a public download with nothing secret inside it.
 */
export default function XBeeSitePairing() {
  const tz = useOrgTimezone();

  const [codes, setCodes] = useState<PairingCode[]>([]);
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [farmId, setFarmId] = useState<number | ''>('');
  const [siteName, setSiteName] = useState('');
  const [creating, setCreating] = useState(false);

  /**
   * How this unit reaches its site, which decides how long the code has to
   * live.
   *
   * 'onsite' is the original flow — somebody is standing at the machine and
   * types the code within the hour, so the server's 2h default is right and a
   * longer window would only widen the exposure of a live credential.
   *
   * 'ship' is a Pi built here, imaged, boxed and couriered. That is days to
   * weeks between minting the code and anything using it, so a 2h code
   * guarantees the unit arrives unpairable.
   */
  const [installMode, setInstallMode] = useState<'onsite' | 'ship'>('onsite');
  const SHIP_TTL_MINUTES = 30 * 24 * 60;

  // Captured at mint time. The form clears itself afterwards, but the kit below
  // still needs to label the files with the site they belong to.
  const [issuedLabel, setIssuedLabel] = useState('');
  const [issuedMode, setIssuedMode] = useState<'onsite' | 'ship'>('onsite');

  // Spent codes are kept by the server on purpose — "that code was already
  // used, on the 3rd, by this coordinator" is the answer to a support call, and
  // a used code nobody on our side redeemed is a signal worth being able to
  // see. Neither is a reason to have them in the way day to day.
  const [showSpent, setShowSpent] = useState(false);

  // The freshly minted code. Held in component state and shown prominently
  // because this is the ONLY time it is displayed in full — after this the list
  // shows it too, but an operator on the phone wants it big and unambiguous.
  const [issued, setIssued] = useState<NewPairingCode | null>(null);
  const [copied, setCopied] = useState(false);

  // The installer build on offer. An admin ringing a farm needs the version
  // string as much as the download itself — "what does it say in Help > About"
  // is the first question when a site is misbehaving.
  const [release, setRelease] = useState<BridgeRelease | null>(null);
  const [downloading, setDownloading] = useState(false);

  // controller_id of the site currently being removed, so only its own row
  // shows a busy state rather than the whole list freezing.
  const [removing, setRemoving] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, farmRes, latest] = await Promise.all([
        espnowService.listPairingCodes(),
        orchardService.getFarms(1, 200).catch(() => null),
        // No release published yet is a normal state on a fresh environment,
        // not something to fail the whole panel over.
        espnowService.getLatestBridgeRelease().catch(() => null),
      ]);
      setCodes(list);
      setRelease(latest);
      if (farmRes?.success) {
        setFarms(farmRes.data?.farms?.map((f: { id: number; name: string }) => ({ id: f.id, name: f.name })) ?? []);
      }
    } catch (err) {
      setError(errorMessage(err, 'Could not load pairing codes'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate() {
    if (farmId === '') {
      setError('Choose which farm this bridge will run.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const label = siteName.trim()
        || farms.find(f => f.id === Number(farmId))?.name
        || 'this site';
      const created = await espnowService.createPairingCode({
        farm_id: Number(farmId),
        controller_name: siteName.trim() || null,
        transport: 'xbee',
        // Omitted for 'onsite' so the server's own default applies rather than
        // this component having an opinion about what "soon" means.
        ...(installMode === 'ship' ? { ttl_minutes: SHIP_TTL_MINUTES } : {}),
      });
      setIssued(created);
      setIssuedLabel(label);
      setIssuedMode(installMode);
      setShowForm(false);
      setSiteName('');
      setFarmId('');
      await load();
    } catch (err) {
      // The server rejects a second open code for the same farm, and a farm
      // that already has an XBee controller. Both are worth showing verbatim
      // rather than flattening to "failed".
      setError(errorMessage(err, 'Could not create a pairing code'));
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(code: string) {
    setError(null);
    try {
      await espnowService.revokePairingCode(code);
      setCodes(prev => prev.filter(c => c.code !== code));
      if (issued?.code === code) setIssued(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not revoke that code'));
    }
  }

  async function handleDownloadInstaller() {
    setDownloading(true);
    setError(null);
    try {
      const blob = await espnowService.downloadBridgeInstaller();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // No version in the filename. `--install` writes whatever it was run
      // from into the Windows scheduled tasks, so a versioned download left
      // every site with a task naming a file the updater would later fill with
      // a different build — and pointed the tasks at a new file each time
      // somebody downloaded a fresh installer. The version is shown next to
      // this button; it does not belong in the name.
      a.download = 'FactreeIrrigationBridge.exe';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoking immediately can cancel the download in some browsers; a tick
      // later is enough for the navigation to have taken the blob.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(errorMessage(err, 'Could not download the installer'));
    } finally {
      setDownloading(false);
    }
  }

  /**
   * Remove a paired site, coordinator and all.
   *
   * Lives here rather than only in the controller list above because this is
   * the panel an operator is looking at when they decide a laptop is finished
   * with, and because deleting the controller is the ONLY way to free the farm:
   * one XBee coordinator per farm is enforced by a unique index, so a farm
   * whose laptop was replaced cannot be paired again until the old site goes.
   *
   * Deliberately not offered as "revoke" - the code is spent and revoking it
   * would leave the controller, its nodes and its programs in place.
   */
  async function handleDeleteSite(c: PairingCode) {
    if (!c.controller_id) return;
    const label = c.controller_name || c.farm_name || c.controller_id;
    if (!confirm(
      `Permanently remove "${label}" (${c.controller_id})?\n\n`
      + 'This deletes the site, its nodes, watering programs, schedules, '
      + 'event history and any sensors on it along with their recorded '
      + 'readings, and frees the farm for a new laptop. It cannot be undone.\n\n'
      + 'The laptop itself keeps watering from its local copy until someone stops it.'
    )) return;

    setRemoving(c.controller_id);
    setError(null);
    try {
      await espnowService.adminDeleteController(c.controller_id);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not remove that site'));
    } finally {
      setRemoving(null);
    }
  }

  async function copyCode(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked on insecure origins and in some embedded
      // webviews. The code is on screen regardless, so this is not an error
      // worth interrupting the operator with.
    }
  }

  function statusOf(c: PairingCode): { label: string; tone: string } {
    if (c.claimed_at) return { label: 'Paired', tone: 'text-green-600 dark:text-green-400' };
    if (new Date(c.expires_at) <= new Date()) return { label: 'Expired', tone: 'text-gray-400' };
    return { label: 'Waiting for the bridge', tone: 'text-blue-600 dark:text-blue-400' };
  }

  /**
   * A code is "spent" once it can never be used again — redeemed, or expired
   * unredeemed. Those rows are history, not work in front of anyone.
   *
   * Hidden rather than deleted. An expired-unused code means re-issue; a used
   * one that nobody on our side redeemed means somebody else has the device.
   * They need to look different, and neither can be told apart from a row that
   * is not there.
   *
   * A live site keeps its row visible regardless: it is still the only place
   * the coordinator address is shown, and removing the site is done from here.
   */
  const { visibleCodes, spentCount } = useMemo(() => {
    const now = Date.now();
    const isLiveSite = (c: PairingCode) => Boolean(c.controller_id);
    const isSpent = (c: PairingCode) =>
      !isLiveSite(c) && (Boolean(c.claimed_at) || new Date(c.expires_at).getTime() <= now);

    const spent = codes.filter(isSpent);
    return {
      visibleCodes: showSpent ? codes : codes.filter(c => !isSpent(c)),
      spentCount: spent.length,
    };
  }, [codes, showSpent]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2">
          <Laptop className="h-5 w-5" />
          XBee sites
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load} aria-label="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => { setShowForm(v => !v); setIssued(null); }}>
            <Plus className="h-4 w-4 mr-1" />
            Add XBee site
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          A laptop or a Raspberry Pi running the irrigation bridge, wired to an
          XBee coordinator. Generate a code here, then either run{' '}
          <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-xs">
            FactreeIrrigationBridge.exe --install
          </code>{' '}
          on the laptop and enter it when prompted, or — for a Pi built here and
          sent out — put the generated files on the card and let it pair itself.
        </p>

        {/* First install. The operator has no controller API key yet — that is
            what pairing issues — so this rides the admin's own session. */}
        <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="min-w-0">
            <div className="text-sm font-medium">Bridge installer</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {release
                ? <>Version {release.version} · {(toNumOr(release.size_bytes) / 1048576).toFixed(1)} MB · published {formatDateTimeInTz(release.uploaded_at, tz)}</>
                : 'No build published yet — CI pushes one on every clean build of main.'}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadInstaller}
            isLoading={downloading}
            disabled={downloading || !release}
          >
            <Download className="h-4 w-4 mr-1" />
            Download installer
          </Button>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* A unit being built to ship needs files, not a number to read out. */}
        {issued && issuedMode === 'ship' && (
          <XBeeProvisioningKit
            issued={issued}
            siteLabel={issuedLabel}
            expiresLabel={formatDateTimeInTz(issued.expires_at, tz)}
          />
        )}

        {/* The code, immediately after minting. */}
        {issued && issuedMode === 'onsite' && (
          <div className="p-4 rounded-lg border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20">
            <div className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
              Enter this on the laptop:
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-3xl tracking-widest font-bold text-blue-900 dark:text-blue-100">
                {issued.display_code}
              </span>
              <Button variant="outline" size="sm" onClick={() => copyCode(issued.display_code)}>
                {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <div className="flex items-center gap-1 mt-2 text-xs text-blue-800 dark:text-blue-300">
              <Clock className="h-3 w-3" />
              Single use, expires {formatDateTimeInTz(issued.expires_at, tz)}
            </div>
          </div>
        )}

        {showForm && (
          <div className="p-4 rounded-lg border border-gray-200 space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="pairing-farm">
                Farm
              </label>
              <select
                id="pairing-farm"
                className="w-full rounded-md border border-gray-300 bg-white text-gray-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={farmId}
                onChange={e => setFarmId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">Select a farm…</option>
                {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                One XBee laptop per farm. Pairing a second one is rejected.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="pairing-name">
                Site name <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <input
                id="pairing-name"
                className="w-full rounded-md border border-gray-300 bg-white text-gray-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Home block shed"
                value={siteName}
                onChange={e => setSiteName(e.target.value)}
                maxLength={64}
              />
            </div>

            <fieldset>
              <legend className="block text-sm font-medium mb-1.5">
                How will this be installed?
              </legend>
              <div className="space-y-2">
                {([
                  {
                    id: 'onsite' as const,
                    title: 'Someone is at the site now',
                    detail: 'A laptop, or a Pi you are sitting in front of. Code expires in 2 hours.',
                  },
                  {
                    id: 'ship' as const,
                    title: 'Building a unit to send out',
                    detail: 'A Pi imaged here and couriered. Code lasts 30 days, and you get the files to put on the card.',
                  },
                ]).map(opt => (
                  <label
                    key={opt.id}
                    className={`flex items-start gap-2.5 p-2.5 rounded-md border cursor-pointer ${
                      installMode === opt.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="install-mode"
                      className="mt-1 accent-blue-600"
                      checked={installMode === opt.id}
                      onChange={() => setInstallMode(opt.id)}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{opt.title}</span>
                      <span className="block text-xs text-gray-500 mt-0.5">{opt.detail}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="flex gap-2">
              <Button onClick={handleCreate} isLoading={creating} disabled={creating}>
                Generate code
              </Button>
              <Button variant="ghost" onClick={() => { setShowForm(false); setError(null); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Existing codes */}
        {loading ? (
          <div className="py-6 text-center text-sm text-gray-500">Loading…</div>
        ) : visibleCodes.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500">
            {codes.length === 0
              ? 'No XBee sites yet.'
              : 'Nothing outstanding — every code has been used or has expired.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {visibleCodes.map(c => {
              const status = statusOf(c);
              return (
                <div key={c.code} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-semibold tracking-wider">{c.display_code}</span>
                      <span className={`text-xs font-medium ${status.tone}`}>{status.label}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {c.farm_name ?? 'No farm'}
                      {c.controller_name ? ` · ${c.controller_name}` : ''}
                      {c.claimed_at
                        ? ` · paired ${formatDateTimeInTz(c.claimed_at, tz)}`
                        : ` · expires ${formatDateTimeInTz(c.expires_at, tz)}`}
                    </div>
                    {c.controller_id && (
                      <div className="text-xs font-mono text-gray-400 mt-0.5">{c.controller_id}</div>
                    )}
                  </div>

                  {/* An unclaimed code is just a code, so it is revoked. A
                      paired site is a controller with nodes, programs and
                      history behind it, so it is removed - a different verb for
                      a genuinely different action. */}
                  {!c.claimed_at ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevoke(c.code)}
                      aria-label={`Revoke code ${c.display_code}`}
                      title="Revoke this unused code"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : c.controller_id ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteSite(c)}
                      isLoading={removing === c.controller_id}
                      disabled={removing !== null}
                      aria-label={`Remove site ${c.controller_name || c.controller_id}`}
                      title="Remove this site and free the farm for a new laptop"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {spentCount > 0 && (
          <button
            type="button"
            onClick={() => setShowSpent(v => !v)}
            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline underline-offset-2"
          >
            {showSpent
              ? 'Hide used and expired codes'
              : `Show ${spentCount} used or expired code${spentCount === 1 ? '' : 's'}`}
          </button>
        )}
      </CardContent>
    </Card>
  );
}
